/**
 * SessionCountdown.js — HH:MM:SS countdown to the 24-hour session window.
 *
 * #162. Previously rendered inline in Thread.js as a 1-minute ticker with
 * HH:MM format. Extracted into its own component for two reasons:
 *
 *   1. A 1-second tick for seconds-precision display would thrash the
 *      whole Thread component's render pipeline (hundreds of bubbles,
 *      reactions, store subscriptions). Isolating it here limits the
 *      re-render blast to ~4 DOM nodes.
 *
 *   2. The blinking-colon effect (digital-clock feel) is easier to
 *      reason about as a self-contained component with its own state.
 *
 * Visual rules:
 *   - Format: HH:MM:SS. No "d" or "h" suffix — we're always within 24h.
 *   - Color ramp: gray (var(--text-muted)) when >1h, amber (#f59e0b)
 *     when <1h, red (#ef4444) when the window has closed.
 *   - Colons blink at 1s cadence via a CSS keyframe — hard toggle,
 *     steps(2), so it feels like a digital clock rather than a pulse.
 *   - When lastInboundTs is unknown/missing, renders nothing (the
 *     parent decides what to show instead).
 */

import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';

const SESSION_WINDOW_HOURS = 24;

export function SessionCountdown({ lastInboundTs }) {
  // Tick every 1s while mounted. State is just a counter — the render
  // function re-computes the remaining seconds from lastInboundTs
  // each time, so there's no risk of drift.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastInboundTs) return;
    const id = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, [lastInboundTs]);

  if (!lastInboundTs) return null;

  const nowSec = Math.floor(Date.now() / 1000);
  const remaining = Math.max(0, (lastInboundTs + SESSION_WINDOW_HOURS * 3600) - nowSec);

  if (remaining <= 0) {
    return html`
      <span
        class="inbox-session-countdown inbox-session-countdown-closed"
        title="Session window closed — waiting for customer message"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
        Closed
      </span>
    `;
  }

  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const pad = (n) => String(n).padStart(2, '0');

  const expiryLabel = new Date((lastInboundTs + 24 * 3600) * 1000).toLocaleString();
  const urgent      = remaining < 3600;

  return html`
    <span
      class=${`inbox-session-countdown${urgent ? ' inbox-session-countdown-urgent' : ''}`}
      title=${`Session window expires ${expiryLabel}`}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
      <span class="inbox-countdown-digits">
        <span>${pad(h)}</span><span class="inbox-countdown-colon">:</span><span>${pad(m)}</span><span class="inbox-countdown-colon">:</span><span>${pad(s)}</span>
      </span>
    </span>
  `;
}
